import { Box, Typography, IconButton, Tooltip, TextField, Select, MenuItem } from '@mui/material';
import {
  Search as SearchIcon,
  BookmarkBorder as SavedSearchIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Clear as ClearIcon,
  CheckCircle as AcceptIcon,
  Cancel as NotAcceptingIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  LocationOn as LocationIcon,
  VerticalSplit as SplitIcon,
  ScreenshotMonitor as MonitorIcon,
} from '@mui/icons-material';
import { MonitorConfigDialog } from '../multi-monitor/MonitorConfigDialog';
import { useMonitorManager } from '../multi-monitor/useMonitorManager';
import { useAuth } from '../auth/useAuth';
import { gwtStyles, getActiveThemeMode, setActiveThemeMode } from '../../config/theme';
import { useUIStore } from '../../stores/uiStore';
import { api } from '../../api/rpc';
import { pushError } from '../../stores/notificationStore';
import { useState, useCallback, useMemo, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReadingTempoWidget } from './ReadingTempoWidget';
import { useCounters } from '../worklist/useCounters';
import { RoentgenToggle } from '../roentgen/RoentgenToggle';

interface HeaderBarProps {
  height: number;
  isNarrow: boolean;
}

/**
 * GWT-matching header bar — mirrors topPanel FlexTable.
 *
 * Single-line (>= 1500px, 36px):
 *   [Logo] [QuickSearch] [spacer] [UserLabel] [AcceptStudies] [AdvSearch] [Prefs] [Logout]
 *
 * Double-line (< 1500px, 72px):
 *   Row 0: [Logo] [QuickSearch] [spacer] [UserLabel] [AcceptStudies] [Logout]
 *   Row 1: [spacer]                                   [AdvSearch]     [Prefs]
 */
export const HeaderBar = ({ height, isNarrow }: HeaderBarProps) => {
  const { user, logout, updateUser } = useAuth();
  const setQuickSearch = useUIStore((s) => s.setQuickSearch);
  const setAdvancedSearchOpen = useUIStore((s) => s.setAdvancedSearchOpen);
  const setPreferencesOpen = useUIStore((s) => s.setPreferencesOpen);
  const setSavedSearchOpen = useUIStore((s) => s.setSavedSearchOpen);
  const [searchField, setSearchField] = useState('smart');
  const [searchText, setSearchText] = useState('');
  const [toggling, setToggling] = useState(false);
  const [monitorConfigOpen, setMonitorConfigOpen] = useState(false);
  const { manager } = useMonitorManager();

  // Queue remaining for reading tempo widget
  const { data: counters } = useCounters();
  const activeTab = useUIStore((s) => s.activeTab);
  const queueRemaining = counters?.[activeTab] ?? undefined;

  // Split-screen toggle
  const splitScreen = useUIStore((s) => s.splitScreen);
  const toggleSplitScreen = useUIStore((s) => s.toggleSplitScreen);

  const handleSearch = useCallback(() => {
    setQuickSearch(searchText.trim(), searchField);
  }, [searchText, searchField, setQuickSearch]);

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchText('');
    setQuickSearch('', searchField);
  }, [searchField, setQuickSearch]);

  const isAccepting = user?.active ?? false;

  // Reading location
  const { data: locationsData } = useQuery({
    queryKey: ['availableReadingLocations'],
    queryFn: () => api.getAvailableReadingLocations(),
    staleTime: 300_000,
  });
  const { data: currentLocData } = useQuery({
    queryKey: ['currentReadingLocation'],
    queryFn: () => api.getCurrentReadingLocation(),
    staleTime: 60_000,
  });
  const locations = useMemo(() => locationsData ?? [], [locationsData]);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const currentLocId = locationId ?? currentLocData?.id ?? '';

  const handleLocationChange = useCallback(async (id: string) => {
    setLocationId(id);
    const loc = locations.find((l) => l.id === id);
    try {
      await api.saveCurrentLocationToSession(loc);
    } catch (err) {
      pushError(err instanceof Error ? err.message : 'Failed to save location');
    }
  }, [locations]);

  const handleToggleAccepting = useCallback(async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const newActive = !isAccepting;
      await api.setUserAttribute('active', String(newActive));
      updateUser({ active: newActive });
    } catch (err) {
      pushError(err instanceof Error ? err.message : 'Failed to toggle accepting studies');
    } finally {
      setToggling(false);
    }
  }, [isAccepting, toggling, updateUser]);

  const userName = user?.name || user?.user || '';
  const userRole = user?.subType || '';
  const isDark = getActiveThemeMode() === 'dark';

  const toggleTheme = useCallback(() => {
    const next = getActiveThemeMode() === 'dark' ? 'legacy' : 'dark';
    setActiveThemeMode(next);
    window.dispatchEvent(new CustomEvent('brit:theme-change', { detail: next }));
  }, []);

  if (isNarrow) {
    // Double-line layout (72px)
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: gwtStyles.headerBg,
          borderBottom: `1px solid ${gwtStyles.borderSubtle}`,
          px: 0.5,
        }}
      >
        {/* Row 0 */}
        <Box sx={{ height: 36, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Logo />
          <QuickSearch
            field={searchField}
            text={searchText}
            onFieldChange={setSearchField}
            onTextChange={setSearchText}
            onKeyDown={handleSearchKeyDown}
            onClear={handleClearSearch}
          />
          <Box sx={{ flex: 1 }} />
          <UserLabel name={userName} role={userRole} />
          <ReadingTempoWidget queueRemaining={queueRemaining} />
          {locations.length > 0 && (
            <ReadingLocationSelector locations={locations} value={currentLocId} onChange={handleLocationChange} />
          )}
          <HeaderButton
            icon={isAccepting ? <AcceptIcon sx={{ fontSize: 14, color: 'success.main' }} /> : <NotAcceptingIcon sx={{ fontSize: 14, color: 'error.main' }} />}
            label={isAccepting ? 'Accepting Studies' : 'Not Accepting'}
            onClick={handleToggleAccepting}
          />
          <RoentgenToggle />
          <HeaderButton icon={<LogoutIcon />} label="Logout" onClick={logout} />
        </Box>
        {/* Row 1 */}
        <Box sx={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
          <HeaderButton icon={<SearchIcon />} label="Advanced Search" onClick={() => setAdvancedSearchOpen(true)} />
          <HeaderButton icon={<SavedSearchIcon />} label="Saved Searches" onClick={() => setSavedSearchOpen(true)} />
          <HeaderButton icon={<SplitIcon sx={splitScreen ? { color: 'primary.main' } : undefined} />} label={splitScreen ? 'Exit Split View' : 'Split View'} onClick={toggleSplitScreen} />
          <HeaderButton icon={<MonitorIcon />} label="Multi-Monitor" onClick={() => setMonitorConfigOpen(true)} />
          <HeaderButton icon={<SettingsIcon />} label="User Preferences" onClick={() => setPreferencesOpen(true)} />
          <HeaderButton icon={isDark ? <LightModeIcon /> : <DarkModeIcon />} label={isDark ? 'Legacy Theme' : 'Dark Theme'} onClick={toggleTheme} />
        </Box>
        <MonitorConfigDialog open={monitorConfigOpen} onClose={() => setMonitorConfigOpen(false)} manager={manager} />
      </Box>
    );
  }

  // Single-line layout (36px)
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: gwtStyles.headerBg,
        borderBottom: `1px solid ${gwtStyles.borderSubtle}`,
        px: 0.5,
        gap: 1,
      }}
    >
      <Logo />
      <QuickSearch
        field={searchField}
        text={searchText}
        onFieldChange={setSearchField}
        onTextChange={setSearchText}
        onKeyDown={handleSearchKeyDown}
        onClear={handleClearSearch}
      />
      <Box sx={{ flex: 1 }} />
      <UserLabel name={userName} role={userRole} />
      <ReadingTempoWidget queueRemaining={queueRemaining} />
      {locations.length > 0 && (
        <ReadingLocationSelector locations={locations} value={currentLocId} onChange={handleLocationChange} />
      )}
      <HeaderButton
        icon={isAccepting ? <AcceptIcon sx={{ color: 'success.main' }} /> : <NotAcceptingIcon sx={{ color: 'error.main' }} />}
        label={isAccepting ? 'Accepting Studies' : 'Not Accepting'}
        onClick={handleToggleAccepting}
      />
      <HeaderButton icon={<SearchIcon />} label="Advanced Search" onClick={() => setAdvancedSearchOpen(true)} />
      <HeaderButton icon={<SavedSearchIcon />} label="Saved Searches" onClick={() => setSavedSearchOpen(true)} />
      <HeaderButton icon={<SplitIcon sx={splitScreen ? { color: 'primary.main' } : undefined} />} label={splitScreen ? 'Exit Split View' : 'Split View'} onClick={toggleSplitScreen} />
      <HeaderButton icon={<MonitorIcon />} label="Multi-Monitor" onClick={() => setMonitorConfigOpen(true)} />
      <HeaderButton icon={<SettingsIcon />} label="User Preferences" onClick={() => setPreferencesOpen(true)} />
      <HeaderButton icon={isDark ? <LightModeIcon /> : <DarkModeIcon />} label={isDark ? 'Legacy Theme' : 'Dark Theme'} onClick={toggleTheme} />
      <RoentgenToggle />
      <HeaderButton icon={<LogoutIcon />} label="Logout" onClick={logout} />
      <MonitorConfigDialog open={monitorConfigOpen} onClose={() => setMonitorConfigOpen(false)} manager={manager} />
    </Box>
  );
};

/* ── Sub-components ── */

const Logo = () => {
  const isDark = getActiveThemeMode() === 'dark';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <Box
        component="img"
        src="/icons/brit/rw-logo-single.png"
        alt="Roentgen Works"
        sx={{ height: 28, objectFit: 'contain' }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          // Fallback: show text if logo image missing
          e.currentTarget.style.display = 'none';
        }}
      />
      <Typography
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotate(-12deg)',
          color: '#e53935',
          fontWeight: 900,
          fontSize: 15,
          letterSpacing: '2px',
          lineHeight: 1,
          textTransform: 'uppercase',
          border: '2.5px solid #e53935',
          borderRadius: '4px',
          px: 1,
          py: 0.25,
          pointerEvents: 'none',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          fontFamily: '"Impact", "Arial Narrow", sans-serif',
          backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}
      >
        REACT
      </Typography>
    </Box>
  );
};

interface QuickSearchProps {
  field: string;
  text: string;
  onFieldChange: (v: string) => void;
  onTextChange: (v: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  onClear: () => void;
}

const QuickSearch = ({ field, text, onFieldChange, onTextChange, onKeyDown, onClear }: QuickSearchProps) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Select
      value={field}
      onChange={(e) => onFieldChange(e.target.value)}
      size="small"
      sx={{
        width: 200,
        height: 28,
        fontSize: 12,
        '& .MuiSelect-select': { py: 0.25 },
      }}
    >
      <MenuItem value="smart">Search By Name+DOB/ID/Acc</MenuItem>
      <MenuItem value="name">Search By Name</MenuItem>
      <MenuItem value="id">Search By Id</MenuItem>
      <MenuItem value="acc">Search By Acc#</MenuItem>
    </Select>
    <TextField
      value={text}
      onChange={(e) => onTextChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder="Quick search..."
      size="small"
      sx={{
        width: 180,
        '& .MuiInputBase-root': { height: 28, fontSize: 12 },
        '& .MuiInputBase-input': { py: 0.25 },
      }}
    />
    <IconButton size="small" onClick={onClear} sx={{ width: 20, height: 20 }}>
      <ClearIcon sx={{ fontSize: 14 }} />
    </IconButton>
  </Box>
);

const UserLabel = ({ name, role }: { name: string; role: string }) => (
  <Box sx={{ textAlign: 'right', lineHeight: 1.2, mx: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {name}
    </Typography>
    {role && (
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {role}
      </Typography>
    )}
  </Box>
);

interface HeaderButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

const HeaderButton = ({ icon, label, onClick }: HeaderButtonProps) => (
  <Tooltip title={label}>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        borderRadius: 1,
        px: 0.75,
        py: 0.25,
        gap: 0.5,
        fontSize: 11,
        color: gwtStyles.textSecondary,
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: gwtStyles.textPrimary },
      }}
    >
      {icon}
      <Typography variant="caption" sx={{ fontSize: 11, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline' } }}>
        {label}
      </Typography>
    </IconButton>
  </Tooltip>
);
interface ReadingLocationSelectorProps {
  locations: Array<{ id?: string; name?: string; type?: string }>;
  value: string;
  onChange: (id: string) => void;
}

const ReadingLocationSelector = ({ locations, value, onChange }: ReadingLocationSelectorProps) => (
  <Tooltip title="Current Reading Location">
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <LocationIcon sx={{ fontSize: 14, color: gwtStyles.textSecondary }} />
      <Select
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        sx={{
          fontSize: 11, height: 24, minWidth: 100, maxWidth: 160,
          color: gwtStyles.textSecondary,
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': { py: 0, pl: 0.5 },
        }}
      >
        <MenuItem value=""><em>No location</em></MenuItem>
        {locations.map((loc) => (
          <MenuItem key={loc.id ?? loc.name} value={loc.id ?? ''}>
            {loc.name ?? loc.id}
          </MenuItem>
        ))}
      </Select>
    </Box>
  </Tooltip>
);